import { useEffect, useMemo, useRef, useState } from 'react';
// Import Hooks
import useRequest from './UseRequest';
// Import Services
import { NotificationHttpService } from 'services/http';
// Import Types
import type { Notification } from 'components/Header/types';

const CHANNEL_NAME = 'notification-polling-channel';
const LOCK_NAME = 'notification-polling-leader';
const POLL_INTERVAL = 60000;
// localStorage key that tracks which tab is currently considered the UI-active owner.
const ACTIVE_OWNER_KEY = 'notification-active-owner';
const ACTIVE_OWNER_TTL = 5000;
const ACTIVE_OWNER_HEARTBEAT_INTERVAL = 2000;
// Prefix for per-notification cross-tab claims used to avoid duplicate desktop notifications.
const PUSH_CLAIM_KEY_PREFIX = 'notification-push-claim-';
const PUSH_CLAIM_TTL = 30000;

interface NotificationDataMessage {
    type: 'notification-data';
    data: Notification[];
    count: number;
}

interface NotificationRequestMessage {
    type: 'request-data';
}

interface NewNotificationsMessage {
    type: 'new-notifications';
    data: Notification[];
}

type NotificationMessage = NotificationDataMessage | NotificationRequestMessage | NewNotificationsMessage;

/**
 * Notification polling hook with cross-tab coordination.
 *
 * Architecture:
 * - One "leader" tab polls backend and broadcasts results via BroadcastChannel.
 * - Other tabs act as followers and sync state without polling.
 * - Active tab becomes "notification owner" and is responsible for showing UI toasts.
 * - localStorage is used for:
 *   - active owner arbitration (with TTL heartbeat)
 *   - deduplicating push notifications per EventLogId across tabs
 *
 * Goal:
 * - Prevent duplicate API polling across tabs
 * - Prevent duplicate UI notifications
 * - Ensure only one tab shows toasts at a time
 */

const useNotificationPolling = (
    setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>,
    setUnreadCount: React.Dispatch<React.SetStateAction<number>>,
    onNewNotifications?: (newNotifications: Notification[]) => void,
) => {
    const { doGetRequest } = useRequest();
    const [isLoading, setIsLoading] = useState(false);

    const { getNotificationListRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- polling manages its own lifecycle
            getNotificationListRequest: NotificationHttpService.getNotificationList(),
        }),
        [],
    );

    const latestBroadcastSnapshotRef = useRef<NotificationDataMessage | null>(null);
    const isFetchingRef = useRef(false);
    const isLeaderRef = useRef(false);
    // Prevents emitting "new" events on initial hydration.
    const hasHydratedRef = useRef(false);
    // Baseline used to detect truly new notifications by EventLogId.
    const knownEventLogIdsRef = useRef<Set<string>>(new Set());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isMountedRef = useRef(false);
    const channelRef = useRef<BroadcastChannel | null>(null);
    // Stable per-tab identifier for localStorage arbitration and push claims.
    const tabIdRef = useRef(`tab-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);
    const activeOwnerHeartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Resolver that ends the current lock callback so leadership can be released on unmount.
    const releaseLeaderLockRef = useRef<(() => void) | null>(null);
    // Allows canceling a queued lock request when this tab unmounts before becoming leader.
    const lockRequestAbortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        isMountedRef.current = true;
        channelRef.current = new BroadcastChannel(CHANNEL_NAME);

        const isTabActive = () => document.visibilityState === 'visible' && document.hasFocus();

        // Reads and validates the tab currently allowed to show desktop/toast notifications.
        const getActiveOwner = () => {
            try {
                const rawValue = localStorage.getItem(ACTIVE_OWNER_KEY);
                if (!rawValue) return null;

                const parsedValue = JSON.parse(rawValue) as { tabId?: string; timestamp?: number };
                if (!parsedValue?.tabId || !parsedValue?.timestamp) {
                    localStorage.removeItem(ACTIVE_OWNER_KEY);
                    return null;
                }

                if (Date.now() - parsedValue.timestamp > ACTIVE_OWNER_TTL) {
                    localStorage.removeItem(ACTIVE_OWNER_KEY);
                    return null;
                }

                return parsedValue;
            } catch {
                localStorage.removeItem(ACTIVE_OWNER_KEY);
                return null;
            }
        };

        // Refreshes this tab ownership with a timestamp so stale owners expire automatically.
        const setActiveOwner = () => {
            localStorage.setItem(
                ACTIVE_OWNER_KEY,
                JSON.stringify({
                    tabId: tabIdRef.current,
                    timestamp: Date.now(),
                }),
            );
        };

        // Avoids removing ownership written by a different tab during race conditions.
        const clearActiveOwnerIfSelf = () => {
            const activeOwner = getActiveOwner();
            if (activeOwner?.tabId === tabIdRef.current) {
                localStorage.removeItem(ACTIVE_OWNER_KEY);
            }
        };

        // Keeps this tab as active notification owner while it is focused.
        const startActiveOwnerHeartbeat = () => {
            if (activeOwnerHeartbeatRef.current) return;

            setActiveOwner();
            activeOwnerHeartbeatRef.current = setInterval(setActiveOwner, ACTIVE_OWNER_HEARTBEAT_INTERVAL);
        };

        // Stop writing heartbeats and release ownership if this tab held it.
        const stopActiveOwnerHeartbeat = () => {
            if (activeOwnerHeartbeatRef.current) {
                clearInterval(activeOwnerHeartbeatRef.current);
                activeOwnerHeartbeatRef.current = null;
            }
            clearActiveOwnerIfSelf();
        };

        const syncActiveOwner = () => {
            if (isTabActive()) {
                startActiveOwnerHeartbeat();
            } else {
                stopActiveOwnerHeartbeat();
            }
        };

        // Best-effort cross-tab lock per EventLogId via localStorage write-then-read verification.
        const tryClaimPushNotification = (eventLogId: string) => {
            const key = `${PUSH_CLAIM_KEY_PREFIX}${eventLogId}`;
            const now = Date.now();

            try {
                const rawValue = localStorage.getItem(key);
                if (rawValue) {
                    const parsedValue = JSON.parse(rawValue) as { tabId?: string; expiresAt?: number };
                    // Another tab still holds a valid claim for this event.
                    if (parsedValue?.expiresAt && parsedValue.expiresAt > now) {
                        return false;
                    }
                }

                localStorage.setItem(
                    key,
                    JSON.stringify({
                        tabId: tabIdRef.current,
                        expiresAt: now + PUSH_CLAIM_TTL,
                    }),
                );

                const verificationRawValue = localStorage.getItem(key);
                if (!verificationRawValue) {
                    return false;
                }

                const verificationParsedValue = JSON.parse(verificationRawValue) as {
                    tabId?: string;
                    expiresAt?: number;
                };

                return (
                    verificationParsedValue?.tabId === tabIdRef.current &&
                    Boolean(verificationParsedValue?.expiresAt && verificationParsedValue.expiresAt > now)
                );
            } catch {
                return false;
            }
        };

        // Decides whether this tab is allowed to surface notifications to the user.
        const dispatchNewNotificationsIfOwned = (newNotifications: Notification[]) => {
            if (!onNewNotifications || newNotifications.length === 0) return;

            if (isTabActive()) {
                // Active tab gets immediate ownership and surfaces toaster notifications.
                setActiveOwner();
                onNewNotifications(newNotifications);
                return;
            }

            const activeOwner = getActiveOwner();
            // A different active owner exists; this tab must stay silent.
            if (activeOwner?.tabId && activeOwner.tabId !== tabIdRef.current) {
                return;
            }

            const claimedNotifications = newNotifications.filter((notification) =>
                tryClaimPushNotification(notification.EventLogId),
            );

            if (claimedNotifications.length > 0) {
                // No active owner exists; emit only notifications this tab successfully claimed.
                onNewNotifications(claimedNotifications);
            }
        };

        const visibilityChangeHandler = () => {
            syncActiveOwner();
        };

        const focusHandler = () => {
            syncActiveOwner();
        };

        const blurHandler = () => {
            syncActiveOwner();
        };

        window.addEventListener('focus', focusHandler);
        window.addEventListener('blur', blurHandler);
        document.addEventListener('visibilitychange', visibilityChangeHandler);
        syncActiveOwner();

        const applyData = (sorted: Notification[], count: number) => {
            setNotifications(sorted);
            setUnreadCount(count);
        };

        // Shared path for server payloads and cross-tab state sync payloads.
        // Centralizes state updates from both server fetches and BroadcastChannel messages.
        const applyAndDiffNotificationPayload = (
            sorted: Notification[],
            count: number,
            shouldBroadcast: boolean,
            shouldNotify: boolean,
        ) => {
            applyData(sorted, count);

            // Detect arrivals against the previous baseline to avoid false-positive "new" events.
            const newNotifications = sorted.filter(
                (notification) => !knownEventLogIdsRef.current.has(notification.EventLogId),
            );

            if (shouldNotify && hasHydratedRef.current && newNotifications.length > 0) {
                // Fan out "new event" signal so non-leader tabs can still decide UI ownership.
                channelRef.current?.postMessage({
                    type: 'new-notifications',
                    data: newNotifications,
                } as NewNotificationsMessage);
                dispatchNewNotificationsIfOwned(newNotifications);
            }

            knownEventLogIdsRef.current = new Set(sorted.map((notification) => notification.EventLogId));
            hasHydratedRef.current = true;

            // Keep the latest snapshot so newly opened follower tabs can request immediate sync.
            const message: NotificationDataMessage = { type: 'notification-data', data: sorted, count };
            latestBroadcastSnapshotRef.current = message;

            if (shouldBroadcast) {
                channelRef.current?.postMessage(message);
            }
        };

        const fetchAndBroadcast = () => {
            if (isFetchingRef.current) return;

            isFetchingRef.current = true;
            setIsLoading(true);
            doGetRequest(getNotificationListRequest.request, {
                successCallback: ({ Data, Count }) => {
                    const sorted = [...Data].sort(
                        (a, b) => (new Date(b.CreatedDate).getTime() || 0) - (new Date(a.CreatedDate).getTime() || 0),
                    );
                    // Leader fetch path: broadcast full state and notify about new arrivals.
                    applyAndDiffNotificationPayload(sorted, Count, true, true);
                },
            }).finally(() => {
                setIsLoading(false);
                isFetchingRef.current = false;
            });
        };

        // Listen for messages from other tabs
        channelRef.current.onmessage = (event: MessageEvent<NotificationMessage>) => {
            if (event.data?.type === 'notification-data') {
                // Follower sync path: update state only, never re-notify.
                applyAndDiffNotificationPayload(event.data.data, event.data.count, false, false);
            } else if (
                event.data?.type === 'request-data' &&
                isLeaderRef.current &&
                latestBroadcastSnapshotRef.current
            ) {
                channelRef.current?.postMessage(latestBroadcastSnapshotRef.current);
            } else if (event.data?.type === 'new-notifications') {
                dispatchNewNotificationsIfOwned(event.data.data);
            }
        };

        // Try to become the leader using Web Locks API
        if (navigator.locks) {
            // One controller per effect run so we can abort the queued lock request in cleanup.
            lockRequestAbortControllerRef.current = new AbortController();

            // Queue for leadership — runs immediately if no leader, or when current leader closes
            navigator.locks
                .request(LOCK_NAME, { signal: lockRequestAbortControllerRef.current.signal }, async () => {
                    if (!isMountedRef.current) return;

                    isLeaderRef.current = true;
                    fetchAndBroadcast();
                    if (intervalRef.current) {
                        // extra safety check
                        clearInterval(intervalRef.current);
                    }
                    intervalRef.current = setInterval(fetchAndBroadcast, POLL_INTERVAL);

                    // Hold the lock for the component lifetime; cleanup resolves this promise.
                    await new Promise<void>((resolve) => {
                        releaseLeaderLockRef.current = resolve;
                    });

                    releaseLeaderLockRef.current = null;
                    isLeaderRef.current = false;

                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                })
                .catch((error) => {
                    if (error?.name !== 'AbortError') {
                        console.error(error);
                    }
                });

            // If not the leader after a tick, request current data from the leader
            // the setTimeout ensures this runs after leader assignment happen and broadcast the data
            setTimeout(() => {
                if (!isLeaderRef.current) {
                    channelRef.current?.postMessage({ type: 'request-data' } as NotificationRequestMessage);
                }
            }, 500);
        } else {
            // Fallback for browsers without Web Locks API: poll normally
            fetchAndBroadcast();
            intervalRef.current = setInterval(fetchAndBroadcast, POLL_INTERVAL);
        }

        return () => {
            isMountedRef.current = false;
            // Resolve the lock-holding promise first, then abort queued requests.
            releaseLeaderLockRef.current?.();
            releaseLeaderLockRef.current = null;
            lockRequestAbortControllerRef.current?.abort();
            lockRequestAbortControllerRef.current = null;
            isLeaderRef.current = false;
            stopActiveOwnerHeartbeat();
            window.removeEventListener('focus', focusHandler);
            window.removeEventListener('blur', blurHandler);
            document.removeEventListener('visibilitychange', visibilityChangeHandler);

            channelRef.current?.close();

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [doGetRequest, getNotificationListRequest.request, onNewNotifications, setNotifications, setUnreadCount]);

    const publishCurrentState = (notifications: Notification[], unreadCount: number) => {
        // Keep baseline aligned when local read/unread actions publish updated state.
        knownEventLogIdsRef.current = new Set(notifications.map((notification) => notification.EventLogId));
        hasHydratedRef.current = true;

        if (channelRef.current) {
            const message: NotificationDataMessage = {
                type: 'notification-data',
                data: notifications,
                count: unreadCount,
            };

            latestBroadcastSnapshotRef.current = message;
            channelRef.current.postMessage(message);
        }
    };

    return {
        isLoading,
        publishCurrentState,
    };
};
export default useNotificationPolling;
